import { AlertV2, Modal, ButtonV2 } from '@cimplico/components';
import { useEffect, useState } from 'react';
import { useWorkpaperAccounts } from '../../queries/use-workpaper-accounts';
import WorkpaperAccountsMappingContainer from '../workpaper-accounts-mapping/WorkpaperAccountsMappingContainer';
import { useWorkpaper } from '../../queries/use-workpaper';
import { useEntity } from '../../queries/use-entity';
import { useFormContext } from 'react-hook-form';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../intercom-events';

type MissingAccountMappingsAlertProps = {
    priorWorkpaperId: string;
};

function MissingAccountMappingsAlert({ priorWorkpaperId }: MissingAccountMappingsAlertProps): JSX.Element {
    const [previewMappings, setPreviewMappings] = useState(false);
    const [successfulMappings, setSuccessfulMappings] = useState<{ workpaperId: string; accountsConfirmed: number } | null>(null);
    const workpaperQuery = useWorkpaper(priorWorkpaperId);
    const entityQuery = useEntity(workpaperQuery.data?.workpaper.entity.id);
    const unmappedAccountsQuery = useWorkpaperAccounts(priorWorkpaperId, false);
    const showAlert = (unmappedAccountsQuery.data?.accounts.length ?? 0) > 0;
    const loading = workpaperQuery.isLoading || entityQuery.isLoading || unmappedAccountsQuery.isLoading;

    const { trackEvent } = useIntercom();
    const { setValue } = useFormContext();

    useEffect(() => {
        setValue('disableNext', loading);
    }, [loading]);

    useEffect(() => {
        if (successfulMappings) {
            trackEvent(
                intercomEvents.account.mapWorkpaperAccounts.event,
                intercomEvents.account.mapWorkpaperAccounts.metaData(
                    successfulMappings.workpaperId,
                    successfulMappings.accountsConfirmed,
                    'new workpaper'
                )
            );
            setSuccessfulMappings(null);
        }
    }, [successfulMappings]);

    if (loading) {
        return <></>;
    }
    if (!workpaperQuery.isSuccess || !entityQuery.isSuccess || !unmappedAccountsQuery.isSuccess) {
        return <></>;
    }

    const workpaper = workpaperQuery.data.workpaper;
    const entity = entityQuery.data.entity;
    const unmappedAccounts = unmappedAccountsQuery.data.accounts;

    return (
        <>
            {previewMappings && (
                <Modal isOpen onClose={() => setPreviewMappings(false)}>
                    <div className="py-8 px-12 w-[1200px] h-[800px] flex flex-col">
                        <div className="overflow-auto grow px-1">
                            <WorkpaperAccountsMappingContainer workpaper={workpaper} entity={entity} onMap={setSuccessfulMappings} />
                        </div>
                        <div className="border-t pt-4 mt-2 flex justify-end gap-2">
                            <ButtonV2
                                isDisabled={unmappedAccounts.length === 0}
                                onClick={() => setPreviewMappings(false)}
                                label="Cancel"
                                size="sm"
                                type="secondary"
                            />
                            <ButtonV2
                                isDisabled={unmappedAccounts.length > 0}
                                onClick={() => setPreviewMappings(false)}
                                label="Finish and close"
                                size="sm"
                                type="primary"
                            />
                        </div>
                    </div>
                </Modal>
            )}
            {showAlert && (
                <AlertV2
                    type="warn"
                    title="Unmapped Accounts"
                    description="The selected workpaper has accounts that have not been mapped to the chart. If you proceed without updating, we will not be able to carry forward these accounts."
                    className="mt-7"
                >
                    <AlertV2.Action
                        label="Update Mappings"
                        onClick={(): void => {
                            setPreviewMappings(true);
                        }}
                    />
                    <AlertV2.ExternalLink
                        label="Learn more"
                        href="https://support.cimplico.com/en/articles/10651539-managing-the-chart-of-accounts"
                    />
                </AlertV2>
            )}
        </>
    );
}
export default MissingAccountMappingsAlert;
